import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Braces, Plus } from 'lucide-react';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import {
  CatalogCard,
  CatalogGrid,
  CatalogGroup,
  CatalogHead,
  CatalogSearch,
  CatalogTable,
  NewCard,
  ViewToggle,
} from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { useViewMode } from '@/features/catalog/useViewMode';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';

import { bySection, useConstants, useFieldTypes, valueText } from './useConstants';

/**
 * Константы — отдельные значения, которые сайт берёт по ключу: почта,
 * соцсети, мессенджеры. Плитками или списком по группам, как остальные
 * разделы; правка — на деталке (замечание Давида: не простыня с правкой в
 * строке).
 */
export function ConstantsPage() {
  const { siteId, env } = useParams({ from: '/authed/sites/$siteId/$env/constants/' });
  const scope = useSiteScope();
  const navigate = useNavigate();
  const [view, setView] = useViewMode('constants');
  const [search, setSearch] = useState('');

  const reader = scope?.can('content.read') ?? false;
  const shaper = scope?.can('site.shape') ?? false;
  const list = useConstants(siteId, env, reader);
  const fieldTypes = useFieldTypes();

  if (scope !== null && !scope.loading && !reader) {
    return <EmptyState title={t.constants.title} description={t.constants.noAccess} />;
  }

  const needle = search.trim().toLowerCase();
  const items = (list.data ?? []).filter(
    (item) => needle === '' || `${item.name} ${item.key}`.toLowerCase().includes(needle),
  );
  const typeTitle = (type: string) =>
    fieldTypes.data?.find((item) => item.type === type)?.title ?? type;
  const create = () => {
    void navigate({ to: '/sites/$siteId/$env/constants/new', params: { siteId, env } });
  };
  const link = (id: string) => ({
    to: '/sites/$siteId/$env/constants/$constantId',
    params: { siteId, env, constantId: id },
  });

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={t.constants.title}
        subtitle={t.constants.subtitle}
        actions={
          <>
            <ViewToggle value={view} onChange={setView} />
            {shaper && (
              <Button iconLeft={<Plus size={15} />} onClick={create}>
                {t.constants.add}
              </Button>
            )}
          </>
        }
      />

      <div className={catalog['bar']}>
        <CatalogSearch
          value={search}
          onChange={setSearch}
          placeholder="Поиск по названию или ключу"
        />
      </div>

      {list.isPending ? null : (list.data ?? []).length === 0 ? (
        <p className={catalog['empty']}>
          {shaper ? t.constants.emptyHint : t.constants.emptyReader}
        </p>
      ) : (
        bySection(items).map(([section, group]) => (
          <CatalogGroup
            key={section}
            title={section === '' ? t.constants.general : section}
            count={group.length}
          >
            {view === 'grid' ? (
              <CatalogGrid>
                {group.map((item) => (
                  <CatalogCard
                    key={item.id}
                    link={link(item.id)}
                    icon={<Braces size={18} />}
                    title={item.name}
                    subtitle={item.key}
                    meta={
                      <>
                        <span>{typeTitle(item.type)}</span>
                        <span>{valueText(item)}</span>
                      </>
                    }
                  />
                ))}
                {shaper && needle === '' && (
                  <NewCard onClick={create}>
                    <Plus size={18} />
                    {t.constants.add}
                  </NewCard>
                )}
              </CatalogGrid>
            ) : (
              <CatalogTable
                head={[t.constants.name, t.constants.key, t.constants.type, 'Значение']}
              >
                {group.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Link {...link(item.id)}>{item.name}</Link>
                    </td>
                    <td className={catalog['mono']}>{item.key}</td>
                    <td>{typeTitle(item.type)}</td>
                    <td>{valueText(item)}</td>
                  </tr>
                ))}
              </CatalogTable>
            )}
          </CatalogGroup>
        ))
      )}
    </div>
  );
}
